import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useI18n } from "../i18n/I18nProvider";
import { Calendar } from "./ui/calendar";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import api from "../lib/api";
import { format, differenceInDays } from "date-fns";
import LicenseUploadForm from "./LicenseUploadForm";
import ContractStep from "./ContractStep";
import { ArrowLeft, ArrowRight, Check, Loader2, Minus, Plus, Sparkles, ShieldCheck, AlertCircle, MapPin, Phone } from "lucide-react";
import { ScooterHelmet } from "./icons/ScooterHelmet";
import { toast } from "sonner";

const SCOOTER_ID = "niu-nqix-500";
const HELMET_SIZES = ["S", "M", "L"];

export default function BookingDialog({ open, onOpenChange, resumeBookingId }) {
  const { t, lang } = useI18n();
  const [step, setStep] = useState(1);
  const [range, setRange] = useState(undefined);
  const [qty, setQty] = useState(1);
  const [helmets, setHelmets] = useState({ S: 0, M: 1, L: 0 });
  const [topcases, setTopcases] = useState(0);
  const [units, setUnits] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [locations, setLocations] = useState([]);
  const [pickupLocationId, setPickupLocationId] = useState(null);
  const [pickupCustomAddress, setPickupCustomAddress] = useState("");
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", notes: "" });
  const [scooter, setScooter] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [availability, setAvailability] = useState({}); // { "YYYY-MM-DD": {available, total} }
  const [bookingId, setBookingId] = useState(null);
  const [signed, setSigned] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [halfDay, setHalfDay] = useState(false);
  const [timeSlot, setTimeSlot] = useState(null); // "morning" | "afternoon" | null

  useEffect(() => {
    if (!open) return;
    api.get(`/scooters/${SCOOTER_ID}`).then(r => setScooter(r.data)).catch(() => {});
    // Fetch next 180 days of availability
    const today = new Date();
    const end = new Date(); end.setDate(today.getDate() + 180);
    const s = format(today, "yyyy-MM-dd");
    const e = format(end, "yyyy-MM-dd");
    api.get(`/scooters/${SCOOTER_ID}/availability`, { params: { start: s, end: e } })
      .then(r => {
        const map = {};
        (r.data.days || []).forEach(d => { map[d.date] = { available: d.available, total: d.total }; });
        setAvailability(map);
      })
      .catch(() => setAvailability({}));
    setStep(1); setDone(null); setHelmets({ S: 0, M: 1, L: 0 }); setQty(1); setTopcases(0); setRange(undefined); setHalfDay(false); setBookingId(null); setSigned(false); setUnits([]); setSelectedUnits([]); setPickupLocationId(null);
    api.get("/locations").then(r => {
      const locs = r.data || [];
      setLocations(locs);
      if (locs.length === 1) setPickupLocationId(locs[0].location_id);
    }).catch(() => setLocations([]));

    // Resume flow: rehydrate state from server
    if (resumeBookingId) {
      setResuming(true);
      api.get(`/bookings/${resumeBookingId}/state`).then(r => {
        const st = r.data;
        setBookingId(st.booking_id);
        setRange({
          from: st.start_date ? new Date(st.start_date + "T00:00:00") : undefined,
          to: st.end_date ? new Date(st.end_date + "T00:00:00") : undefined,
        });
        setQty(st.quantity || 1);
        setSelectedUnits(st.unit_ids || []);
        setHelmets(st.helmets || { S: 0, M: 1, L: 0 });
        setTopcases(st.topcases || 0);
        if (st.pickup_location?.location_id) setPickupLocationId(st.pickup_location.location_id);
        setForm({
          customer_name: st.customer_name || "",
          customer_email: st.customer_email || "",
          customer_phone: st.customer_phone || "",
          notes: "",
        });
        setSigned(!!st.contract_signed);
        if (st.contract_signed) setStep(6);
        else if (st.license_verified) setStep(6);
        else setStep(5);
        toast.success(t.book.resumed || "Prenotazione ripresa");
      }).catch(err => {
        if (err.response?.status === 410 || err.response?.status === 404) {
          localStorage.removeItem("pendingBookingId");
        }
        toast.error(t.book.resume_failed || "Impossibile riprendere la prenotazione");
      }).finally(() => setResuming(false));
    }
  }, [open, resumeBookingId, t]);

  // Persist bookingId to localStorage so the user can resume after closing the dialog.
  useEffect(() => {
    if (bookingId && !done) {
      localStorage.setItem("pendingBookingId", bookingId);
    }
  }, [bookingId, done]);

  // Fetch individual units availability whenever the date range OR the chosen time slot changes.
  // For half-day we ask the backend which units are free for that specific slot only.
  useEffect(() => {
    if (!range?.from || !range?.to) { setUnits([]); setSelectedUnits([]); return; }
    const s = format(range.from, "yyyy-MM-dd");
    const e = format(range.to, "yyyy-MM-dd");
    const slot = (halfDay && timeSlot) ? timeSlot : "full";
    api.get(`/scooters/${SCOOTER_ID}/units`, { params: { start: s, end: e, time_slot: slot } })
      .then(r => {
        const list = r.data.units || [];
        setUnits(list);
        setSelectedUnits(prev => prev.filter(id => list.some(u => u.unit_id === id && u.available)));
      })
      .catch(() => setUnits([]));
  }, [range, halfDay, timeSlot]);

  const setQtyAndHelmets = (newQty) => {
    setQty(prevQty => {
      setHelmets(h => {
        const total = (h.S || 0) + (h.M || 0) + (h.L || 0);
        // When shrinking, clamp the total down to newQty (= 1 helmet per scooter).
        // Any "extra" helmets the user had accumulated for the removed scooters
        // are dropped automatically — otherwise they'd stay and silently charge
        // the customer for helmets they no longer need.
        const targetTotal = newQty < prevQty ? newQty : Math.min(total, 2 * newQty);
        if (total > targetTotal) {
          const next = { ...h };
          let over = total - targetTotal;
          for (const sz of ["L", "M", "S"]) {
            if (over <= 0) break;
            const take = Math.min(over, next[sz] || 0);
            next[sz] = (next[sz] || 0) - take;
            over -= take;
          }
          return next;
        }
        // If growing: top up M-size to at least 1 per scooter
        if (total < newQty) return { ...h, M: (h.M || 0) + (newQty - total) };
        return h;
      });
      return newQty;
    });
  };

  const days = useMemo(() => {
    if (!range?.from || !range?.to) return 1;
    return Math.max(1, differenceInDays(range.to, range.from) + 1);
  }, [range]);

  // ─── Same-day booking rules (mirrors backend) ─────────────────
  // Today: only afternoon half-day (13:00-17:00) is allowed.
  const todayStr = format(new Date(), "yyyy-MM-dd");
  const hourNow = new Date().getHours();
  const startsToday = range?.from && format(range.from, "yyyy-MM-dd") === todayStr;
  const endsToday = range?.to && format(range.to, "yyyy-MM-dd") === todayStr;
  const singleDayToday = startsToday && endsToday;
  const afternoonPast = hourNow >= 17;      // return-by time
  const morningPast = true;                  // morning slot never allowed for same-day
  const halfDayCutoffReached = afternoonPast; // when the afternoon slot has passed today
  // Auto-force half-day + afternoon slot when only today is selected
  useEffect(() => {
    if (singleDayToday) {
      if (!halfDay) setHalfDay(true);
      if (timeSlot !== "afternoon" && !afternoonPast) setTimeSlot("afternoon");
    }
  }, [singleDayToday, halfDay, timeSlot, afternoonPast]);
  // Reset time_slot whenever the user leaves half-day mode
  useEffect(() => {
    if (!halfDay && timeSlot) setTimeSlot(null);
  }, [halfDay, timeSlot]);

  // Tier auto: if days>=3 → multi_day; else if 1 day and halfDay toggle → half_day; else full_day
  const autoTier = days >= 3 ? "multi_day" : (days === 1 && halfDay ? "half_day" : "full_day");
  const helmetsTotal = (helmets.S || 0) + (helmets.M || 0) + (helmets.L || 0);
  const helmetsMax = 2 * qty;
  const extraHelmets = Math.max(0, helmetsTotal - qty);
  const pricing = scooter?.pricing || {};
  const extraHelmetPrice = pricing.extra_helmet ?? 15;
  const helmetFee = extraHelmets * extraHelmetPrice;
  // Small helper to fill in the {token} placeholders from translations with live pricing.
  const fmtPrice = (tpl) => (tpl || "")
    .replace("{full_day}", String(pricing.full_day ?? 85))
    .replace("{half_day}", String(pricing.half_day ?? 50))
    .replace("{multi_day}", String(pricing.multi_day ?? 70))
    .replace("{extra_helmet}", String(extraHelmetPrice));

  const total = useMemo(() => {
    if (!scooter || !range?.from || !range?.to) return 0;
    const p = scooter.pricing || {};
    let rateTotal = 0;
    if (autoTier === "multi_day") rateTotal = (p.multi_day || 0) * days * qty;
    else if (autoTier === "half_day") rateTotal = (p.half_day || 0) * qty;
    else rateTotal = (p.full_day || 0) * days * qty;
    return rateTotal + helmetFee + (topcases * 20);
  }, [scooter, autoTier, days, qty, helmetFee, range, topcases]);

  // Compute minimum available scooters across the selected date range
  const minAvailable = useMemo(() => {
    if (!range?.from || !range?.to) return scooter?.units_total ?? 0;
    let min = scooter?.units_total ?? 999;
    let cur = new Date(range.from);
    const end = new Date(range.to);
    while (cur <= end) {
      const key = format(cur, "yyyy-MM-dd");
      if (availability[key]) {
        min = Math.min(min, availability[key].available);
      }
      cur.setDate(cur.getDate() + 1);
    }
    return min;
  }, [range, availability, scooter]);

  const isRangeUnavailable = range?.from && range?.to && minAvailable === 0;
  // Keep qty (and helmets) in sync with the selected units
  useEffect(() => {
    if (selectedUnits.length > 0) setQtyAndHelmets(selectedUnits.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnits.length]);

  const toggleUnit = (uid) => {
    setSelectedUnits(prev => prev.includes(uid) ? prev.filter(i => i !== uid) : [...prev, uid]);
  };

  // Clamp topcases when qty shrinks
  useEffect(() => {
    if (topcases > qty) setTopcases(qty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qty]);

  const setHelmet = (size, delta) => {
    setHelmets(h => {
      const current = (h[size] || 0) + delta;
      if (current < 0) return h;
      const others = ((h.S || 0) + (h.M || 0) + (h.L || 0)) - (h[size] || 0);
      if (others + current > helmetsMax) return h; // enforce max 2 per scooter
      return { ...h, [size]: current };
    });
  };

  const submit = async () => {
    if (!range?.from || !range?.to) return toast.error("Select dates");
    if (!form.customer_name || !form.customer_email) return toast.error("Name & email required");
    if (!form.customer_phone || form.customer_phone.trim().length < 6) return toast.error(t.book.phone_required);
    setSubmitting(true);
    try {
      const payload = {
        scooter_id: SCOOTER_ID,
        start_date: format(range.from, "yyyy-MM-dd"),
        end_date: format(range.to, "yyyy-MM-dd"),
        quantity: qty,
        unit_ids: selectedUnits,
        pickup_location_id: pickupLocationId,
        pickup_custom_address: pickupCustomAddress || undefined,
        pricing_tier: autoTier,
        time_slot: (autoTier === "half_day" && timeSlot) ? timeSlot : "full",
        locale: lang || "it",
        helmets,
        topcases,
        ...form,
      };
      const { data } = await api.post("/bookings", payload);
      setBookingId(data.booking_id);
      setStep(5);  // advance to license step
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSubmitting(false); }
  };

  const goToPayment = async () => {
    if (!bookingId) return;
    setSubmitting(true);
    try {
      const checkout = await api.post("/payments/checkout", {
        booking_id: bookingId,
        origin_url: window.location.origin,
      });
      if (checkout.data?.url) {
        window.location.href = checkout.data.url;
        return;
      }
      toast.error("Impossibile creare il checkout");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#0a0a0a] border border-white/10 rounded-none text-white p-0" data-testid="booking-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-white/10">
          <DialogTitle className="font-heading font-black text-2xl tracking-tighter">{done ? t.book.success : t.book.title}</DialogTitle>
          <DialogDescription className="sr-only">{t.book.select_dates}</DialogDescription>
          {!done && (
            <div className="flex gap-2 mt-4">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className={`h-1 flex-1 ${step >= i ? "bg-scooter-yellow" : "bg-white/10"}`} />
              ))}
            </div>
          )}
        </DialogHeader>

        {done ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto bg-scooter-yellow text-black flex items-center justify-center mb-6"><Check className="w-8 h-8" /></div>
            <p className="text-zinc-300 mb-2">{t.book.success_d}</p>
            <div className="bg-black border border-white/10 p-4 mt-6 text-sm text-left">
              <div className="flex justify-between py-1"><span className="text-zinc-500">ID</span><span className="font-mono text-xs">{done.booking_id}</span></div>
              <div className="flex justify-between py-1"><span className="text-zinc-500">{t.book.start} → {t.book.end}</span><span>{done.start_date} → {done.end_date}</span></div>
              <div className="flex justify-between py-1"><span className="text-zinc-500">{t.book.qty}</span><span>{done.quantity}× NIU NQiX 500</span></div>
              <div className="flex justify-between py-1"><span className="text-zinc-500">{t.book.total}</span><span className="text-scooter-yellow font-bold">€{done.total_price.toFixed(2)}</span></div>
            </div>
            <button onClick={() => onOpenChange(false)} className="mt-6 bg-scooter-yellow text-black font-bold text-xs tracking-[0.2em] uppercase h-12 px-8" data-testid="booking-close-btn">OK</button>
          </div>
        ) : (
          <>
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {!done && (
                <a
                  href="tel:+393356116528"
                  className="mb-4 flex items-center justify-center gap-2 border border-scooter-yellow/50 bg-scooter-yellow/10 hover:bg-scooter-yellow/20 text-scooter-yellow px-3 py-2 text-[11px] tracking-[0.2em] uppercase font-bold transition-colors"
                  data-testid="custom-hours-banner"
                >
                  <Phone className="w-3.5 h-3.5" />
                  <span>{t.book.custom_hours_call || "Per info e orari personalizzati chiamaci al +39 335 6116528"}</span>
                </a>
              )}
              {step === 1 && (
                <div data-testid="booking-step-1">
                  <Calendar mode="range" numberOfMonths={1} selected={range}
                    onSelect={(r) => {
                      if (r?.from && !r?.to) setRange({ from: r.from, to: r.from });
                      else setRange(r);
                    }}
                    disabled={(d) => {
                      const past = d < new Date(new Date().setHours(0,0,0,0));
                      if (past) return true;
                      const key = format(d, "yyyy-MM-dd");
                      // Today is only bookable for half-day, and only before 14:00 local time
                      if (key === todayStr && halfDayCutoffReached) return true;
                      const av = availability[key];
                      return !!(av && av.available === 0);
                    }}
                    className="mx-auto" />

                  {range?.from && range?.to ? (
                    <div className="mt-5 border border-scooter-yellow/40 bg-scooter-yellow/5 p-4" data-testid="dates-summary">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-1">{t.book.pickup}</div>
                          <div className="font-heading font-bold text-lg text-white" data-testid="pickup-date">{format(range.from, "EEE d MMM")}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-1">{t.book.return}</div>
                          <div className="font-heading font-bold text-lg text-white" data-testid="return-date">
                            {format(range.from, "yyyy-MM-dd") === format(range.to, "yyyy-MM-dd")
                              ? <span className="text-scooter-yellow">{t.book.same_day}</span>
                              : format(range.to, "EEE d MMM")}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-center gap-2 text-xs text-zinc-300">
                        <Sparkles className="w-3 h-3 text-scooter-yellow" />
                        <span data-testid="days-count">{days} {days === 1 ? "day" : "days"}</span>
                        {days >= 3 && <span className="text-scooter-yellow">· {t.pricing.multi}</span>}
                      </div>
                      <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-center gap-2 text-xs" data-testid="availability-info">
                        {isRangeUnavailable ? (
                          <span className="flex items-center gap-2 text-red-400 font-bold uppercase tracking-wider text-[11px]"><AlertCircle className="w-3 h-3" /> {t.book.sold_out}</span>
                        ) : (
                          <span className="text-zinc-400"><span className="text-scooter-yellow font-bold" data-testid="min-available">{minAvailable}</span> / {scooter?.units_total || 0} {t.book.available_label}</span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-5 text-center text-xs text-zinc-400" data-testid="dates-empty">
                      <div className="font-bold tracking-wider uppercase">{t.book.select_dates}</div>
                      <div className="mt-1 text-[11px] text-zinc-500">{t.book.select_dates_hint}</div>
                    </div>
                  )}

                  {range?.from && range?.to && !isRangeUnavailable && days === 1 && (
                    <div className="mt-5" data-testid="duration-choice">
                      <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500 mb-3">{t.book.duration_choice}</div>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => { if (!startsToday) setHalfDay(false); }}
                          disabled={startsToday}
                          data-testid="full-day-btn"
                          className={`p-4 text-left border transition ${startsToday ? "border-white/10 opacity-40 cursor-not-allowed" : !halfDay ? "border-scooter-yellow bg-scooter-yellow/10" : "border-white/15 hover:border-white/30"}`}
                        >
                          <div className={`text-xs tracking-[0.2em] uppercase font-bold ${!halfDay && !startsToday ? "text-scooter-yellow" : "text-zinc-300"}`}>{t.book.full_day_option}</div>
                          <div className="text-[11px] text-white mt-1">{fmtPrice(t.book.full_day_option_d)}</div>
                          {startsToday && <div className="mt-2 text-[10px] text-red-400 uppercase tracking-wider font-bold">{t.book.full_day_same_day_locked}</div>}
                          {!halfDay && !startsToday && <div className="mt-2 text-[10px] text-scooter-yellow uppercase tracking-wider font-bold flex items-center gap-1"><Check className="w-3 h-3" /> {t.book.tier}</div>}
                        </button>
                        <button
                          type="button"
                          onClick={() => setHalfDay(true)}
                          data-testid="half-day-btn"
                          className={`p-4 text-left border transition ${halfDay ? "border-scooter-yellow bg-scooter-yellow/10" : "border-white/15 hover:border-white/30"}`}
                        >
                          <div className={`text-xs tracking-[0.2em] uppercase font-bold ${halfDay ? "text-scooter-yellow" : "text-zinc-300"}`}>{t.book.half_day_option}</div>
                          <div className="text-[11px] text-white mt-1">{fmtPrice(t.book.half_day_option_d)}</div>
                          {halfDay && <div className="mt-2 text-[10px] text-scooter-yellow uppercase tracking-wider font-bold flex items-center gap-1"><Check className="w-3 h-3" /> {t.book.tier}</div>}
                        </button>
                      </div>
                      {halfDay && (
                        <div className="mt-4" data-testid="time-slot-picker">
                          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500 mb-3">{t.book.slot_choice || "Fascia oraria"}</div>
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              type="button"
                              onClick={() => setTimeSlot("morning")}
                              disabled={startsToday && morningPast}
                              data-testid="slot-morning-btn"
                              className={`p-4 text-left border transition ${startsToday && morningPast ? "border-white/10 opacity-40 cursor-not-allowed" : timeSlot === "morning" ? "border-scooter-yellow bg-scooter-yellow/10" : "border-white/15 hover:border-white/30"}`}
                            >
                              <div className={`text-xs tracking-[0.2em] uppercase font-bold ${timeSlot === "morning" ? "text-scooter-yellow" : "text-zinc-300"}`}>{t.book.slot_morning || "Mattina"}</div>
                              <div className="text-[11px] text-white mt-1">08:00 – 12:00</div>
                              {startsToday && morningPast && <div className="mt-2 text-[10px] text-red-400 uppercase tracking-wider font-bold">{t.book.slot_past || "Fascia passata"}</div>}
                              {timeSlot === "morning" && !(startsToday && morningPast) && <div className="mt-2 text-[10px] text-scooter-yellow uppercase tracking-wider font-bold flex items-center gap-1"><Check className="w-3 h-3" /> {t.book.tier}</div>}
                            </button>
                            <button
                              type="button"
                              onClick={() => setTimeSlot("afternoon")}
                              disabled={startsToday && afternoonPast}
                              data-testid="slot-afternoon-btn"
                              className={`p-4 text-left border transition ${startsToday && afternoonPast ? "border-white/10 opacity-40 cursor-not-allowed" : timeSlot === "afternoon" ? "border-scooter-yellow bg-scooter-yellow/10" : "border-white/15 hover:border-white/30"}`}
                            >
                              <div className={`text-xs tracking-[0.2em] uppercase font-bold ${timeSlot === "afternoon" ? "text-scooter-yellow" : "text-zinc-300"}`}>{t.book.slot_afternoon || "Pomeriggio"}</div>
                              <div className="text-[11px] text-white mt-1">13:00 – 17:00</div>
                              {startsToday && afternoonPast && <div className="mt-2 text-[10px] text-red-400 uppercase tracking-wider font-bold">{t.book.slot_past || "Fascia passata"}</div>}
                              {timeSlot === "afternoon" && !(startsToday && afternoonPast) && <div className="mt-2 text-[10px] text-scooter-yellow uppercase tracking-wider font-bold flex items-center gap-1"><Check className="w-3 h-3" /> {t.book.tier}</div>}
                            </button>
                          </div>
                        </div>
                      )}
                      {startsToday && (
                        <p className="mt-3 text-[11px] text-amber-400 flex items-start gap-2" data-testid="same-day-notice">
                          <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          {t.book.same_day_notice}
                        </p>
                      )}
                    </div>
                  )}
                  {startsToday && days > 1 && (
                    <p className="mt-3 text-[11px] text-red-400 flex items-start gap-2" data-testid="today-multi-notice">
                      <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      {t.book.today_multiday_blocked}
                    </p>
                  )}
                </div>
              )}
              {step === 2 && (
                <div className="space-y-6" data-testid="booking-step-2">
                  <div data-testid="unit-picker">
                    <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500 mb-1">{t.book.choose_scooter}</div>
                    <p className="text-[11px] text-zinc-500 mb-3">{t.book.choose_scooter_hint}</p>
                    <div className="grid grid-cols-2 gap-3">
                      {units.map(u => {
                        const sel = selectedUnits.includes(u.unit_id);
                        return (
                          <button
                            key={u.unit_id}
                            type="button"
                            disabled={!u.available}
                            onClick={() => toggleUnit(u.unit_id)}
                            data-testid={`unit-option-${u.unit_id}`}
                            className={`p-4 text-left border transition ${!u.available ? "border-white/10 opacity-40 cursor-not-allowed" : sel ? "border-scooter-yellow bg-scooter-yellow/10" : "border-white/15 hover:border-white/30"}`}
                          >
                            <div className={`font-heading font-black text-lg tracking-tight ${sel ? "text-scooter-yellow" : "text-white"}`}>{u.public_name}</div>
                            <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">NIU NQiX 500</div>
                            {!u.available
                              ? <div className="mt-2 text-[10px] text-red-400 uppercase tracking-wider font-bold" data-testid={`unit-busy-${u.unit_id}`}>{t.book.unit_busy}</div>
                              : sel && <div className="mt-2 text-[10px] text-scooter-yellow uppercase tracking-wider font-bold flex items-center gap-1"><Check className="w-3 h-3" /> {t.book.unit_selected}</div>}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {locations.length > 0 && (
                    <div data-testid="pickup-location-picker">
                      <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500 mb-1">{t.book.pickup_point || "Punto di ritiro e riconsegna"}</div>
                      <p className="text-[11px] text-zinc-500 mb-3">{t.book.pickup_point_hint || "Ritiri e riconsegni lo scooter nello stesso punto."}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {locations.map(l => {
                          const sel = pickupLocationId === l.location_id;
                          return (
                            <button
                              key={l.location_id}
                              type="button"
                              onClick={() => { setPickupLocationId(l.location_id); if (!l.customer_provides_address) setPickupCustomAddress(""); }}
                              data-testid={`location-option-${l.location_id}`}
                              className={`p-4 text-left border transition ${sel ? "border-scooter-yellow bg-scooter-yellow/10" : "border-white/15 hover:border-white/30"}`}
                            >
                              <div className={`flex items-center gap-2 font-heading font-black tracking-tight ${sel ? "text-scooter-yellow" : "text-white"}`}>
                                <MapPin className="w-4 h-4 flex-shrink-0" />{l.name}
                              </div>
                              {l.address && <div className="text-[11px] text-zinc-400 mt-1">{l.address}</div>}
                              {l.customer_provides_address && <div className="text-[10px] text-scooter-yellow mt-1 uppercase tracking-wider font-bold">{t.book.pickup_hotel_hint_short || "Inserisci il nome del tuo hotel"}</div>}
                              {l.notes && <div className="text-[10px] text-zinc-500 mt-0.5">{l.notes}</div>}
                              {sel && !l.customer_provides_address && <div className="mt-2 text-[10px] text-scooter-yellow uppercase tracking-wider font-bold flex items-center gap-1"><Check className="w-3 h-3" /> {t.book.unit_selected}</div>}
                            </button>
                          );
                        })}
                      </div>
                      {(() => {
                        const sel = locations.find(l => l.location_id === pickupLocationId);
                        if (!sel || !sel.customer_provides_address) return null;
                        return (
                          <div className="mt-4 border border-scooter-yellow/40 bg-scooter-yellow/5 p-4" data-testid="pickup-hotel-block">
                            <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-scooter-yellow">{t.book.pickup_hotel_label || "Nome del tuo hotel *"}</Label>
                            <Input
                              value={pickupCustomAddress}
                              onChange={e => setPickupCustomAddress(e.target.value)}
                              placeholder={t.book.pickup_hotel_placeholder || "Es. Hotel La Perla, Corvara"}
                              className="mt-2 bg-transparent border-scooter-yellow/50 rounded-none focus-visible:ring-scooter-yellow focus-visible:border-scooter-yellow text-white"
                              data-testid="pickup-hotel-input"
                            />
                            <p className="text-[11px] text-zinc-400 mt-2">{t.book.pickup_hotel_hint || "Verremo a consegnarti lo scooter direttamente presso l'hotel indicato."}</p>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
              {step === 3 && (
                <div className="space-y-6" data-testid="booking-step-3">
                  <div>
                    <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.book.your_scooters}</Label>
                    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="selected-units">
                      {selectedUnits.map(uid => {
                        const u = units.find(x => x.unit_id === uid);
                        return <span key={uid} className="px-3 py-2 border border-scooter-yellow/50 bg-scooter-yellow/10 text-scooter-yellow font-heading font-bold text-sm" data-testid={`selected-unit-${uid}`}>{u?.public_name || uid}</span>;
                      })}
                      <span className="text-xs text-zinc-500" data-testid="qty-value">{qty}× NIU NQiX 500</span>
                    </div>
                  </div>

                  <div className="border border-white/10 p-5" data-testid="helmets-section">
                    <div className="flex items-center gap-2 mb-1">
                      <ScooterHelmet className="w-4 h-4 text-scooter-yellow" />
                      <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-scooter-yellow">{t.book.helmets_title}</span>
                    </div>
                    <p className="text-xs text-zinc-400 mb-4">{fmtPrice(t.book.helmets_sub)}</p>
                    <div className="grid grid-cols-3 gap-3">
                      {HELMET_SIZES.map(size => (
                        <div key={size} className="border border-white/10 p-3 text-center" data-testid={`helmet-${size}`}>
                          <div className="text-2xl font-heading font-black tracking-tighter text-white mb-2">{size}</div>
                          <div className="flex items-center justify-center gap-2">
                            <button onClick={() => setHelmet(size, -1)} className="w-8 h-8 border border-white/20 hover:border-scooter-yellow flex items-center justify-center" data-testid={`helmet-${size}-minus`}><Minus className="w-3 h-3" /></button>
                            <span className="font-heading font-bold text-xl w-8 text-center" data-testid={`helmet-${size}-value`}>{helmets[size] || 0}</span>
                            <button onClick={() => setHelmet(size, 1)} className="w-8 h-8 border border-white/20 hover:border-scooter-yellow flex items-center justify-center" data-testid={`helmet-${size}-plus`}><Plus className="w-3 h-3" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex items-center justify-between text-xs">
                      <span className="text-zinc-500 uppercase tracking-wider font-bold">{t.book.helmets_count}</span>
                      <span className={`font-heading font-bold text-lg ${helmetsTotal < qty ? "text-amber-400" : "text-scooter-yellow"}`} data-testid="helmets-total">{helmetsTotal} / {helmetsMax}</span>
                    </div>
                    {helmetsTotal < qty && (
                      <p className="text-[11px] text-amber-400/80 mt-1">{t.book.helmets_warn}</p>
                    )}
                    {helmetsTotal >= helmetsMax && (
                      <p className="text-[11px] text-zinc-500 mt-1">{t.book.helmets_max}</p>
                    )}
                    {extraHelmets > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between text-xs">
                        <span className="text-zinc-400">{t.book.helmet_fee_line} ({extraHelmets} × €{extraHelmetPrice})</span>
                        <span className="text-scooter-yellow font-bold" data-testid="helmet-fee">+€{helmetFee.toFixed(2)}</span>
                      </div>
                    )}
                  </div>

                  <div className="border border-scooter-yellow/30 bg-scooter-yellow/5 p-5">
                    <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-bold text-scooter-yellow mb-2">
                      <Sparkles className="w-3 h-3" /> {t.book.tier}
                    </div>
                    <div className="font-heading font-bold text-2xl text-white">
                      {autoTier === "multi_day" ? `${t.pricing.multi} · €${pricing.multi_day ?? 70}${t.pricing.per_day}` : autoTier === "half_day" ? `${t.pricing.half} · €${pricing.half_day ?? 50} ${t.pricing.flat}` : `${t.pricing.full} · €${pricing.full_day ?? 85}${t.pricing.per_day}`}
                    </div>
                    <div className="text-xs text-zinc-400 mt-1">{t.book.auto_tier}</div>
                  </div>
                </div>
              )}
              {step === 4 && (
                <div className="space-y-4" data-testid="booking-step-4">
                  <div>
                    <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.book.name}</Label>
                    <Input value={form.customer_name} onChange={(e) => setForm(f => ({...f, customer_name: e.target.value}))} className="mt-2 bg-transparent border-white/20 rounded-none focus-visible:ring-scooter-yellow focus-visible:border-scooter-yellow text-white" data-testid="input-name" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.book.email}</Label>
                      <Input type="email" value={form.customer_email} onChange={(e) => setForm(f => ({...f, customer_email: e.target.value}))} className="mt-2 bg-transparent border-white/20 rounded-none focus-visible:ring-scooter-yellow focus-visible:border-scooter-yellow text-white" data-testid="input-email" />
                    </div>
                    <div>
                      <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-scooter-yellow">{t.book.phone} *</Label>
                      <Input value={form.customer_phone} onChange={(e) => setForm(f => ({...f, customer_phone: e.target.value}))} placeholder="+39 335 1234567" className="mt-2 bg-transparent border-scooter-yellow/40 rounded-none focus-visible:ring-scooter-yellow focus-visible:border-scooter-yellow text-white" data-testid="input-phone" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.book.notes}</Label>
                    <Textarea value={form.notes} onChange={(e) => setForm(f => ({...f, notes: e.target.value}))} className="mt-2 bg-transparent border-white/20 rounded-none focus-visible:ring-scooter-yellow focus-visible:border-scooter-yellow text-white" data-testid="input-notes" />
                  </div>
                  <div className="text-center text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500 mt-2 flex items-center justify-center gap-2" data-testid="stripe-hint">
                    <ShieldCheck className="w-3 h-3 text-scooter-yellow" />
                    {t.book.pay_redirect}
                  </div>
                </div>
              )}

              {step === 5 && bookingId && (
                <div data-testid="booking-step-5">
                  <LicenseUploadForm
                    bookingId={bookingId}
                    submitLabel={t.book.continue_sign || t.book.continue_pay}
                    onDone={() => setStep(6)}
                  />
                </div>
              )}

              {step === 6 && bookingId && (
                <ContractStep
                  bookingId={bookingId}
                  signed={signed}
                  setSigned={setSigned}
                  onProceed={goToPayment}
                  submitting={submitting}
                  total={total}
                  t={t}
                />
              )}
            </div>

            <div className="border-t border-white/10 px-6 py-4 flex items-center justify-between gap-4">
              <div className="text-sm">
                <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.book.total}</div>
                <div className="font-heading font-black text-2xl text-scooter-yellow" data-testid="booking-total">€{total.toFixed(2)}</div>
              </div>
              <div className="flex gap-2">
                {step > 1 && step < 5 && <button onClick={() => setStep(s => s-1)} className="h-12 px-5 border border-white/20 text-xs font-bold tracking-[0.2em] uppercase hover:bg-white/5 flex items-center gap-2" data-testid="booking-back-btn"><ArrowLeft className="w-3 h-3" />{t.book.back}</button>}
                {step < 4 && <button onClick={() => setStep(s => s+1)} disabled={(step === 1 && (!range?.from || !range?.to || isRangeUnavailable || (startsToday && (days > 1 || !halfDay)) || (halfDay && !timeSlot))) || (step === 2 && (selectedUnits.length === 0 || (locations.length > 0 && !pickupLocationId) || (() => { const sel = locations.find(l => l.location_id === pickupLocationId); return sel && sel.customer_provides_address && !pickupCustomAddress.trim(); })()))} className="h-12 px-6 bg-scooter-yellow text-black font-bold text-xs tracking-[0.2em] uppercase hover:bg-scooter-yellow-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2" data-testid="booking-next-btn">{t.book.next}<ArrowRight className="w-3 h-3" /></button>}
                {step === 4 && <button onClick={submit} disabled={submitting} className="h-12 px-6 bg-scooter-yellow text-black font-bold text-xs tracking-[0.2em] uppercase hover:bg-scooter-yellow-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2" data-testid="booking-confirm-btn">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-3 h-3" />}{t.book.continue_license}</button>}
                {step === 5 && <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{t.book.step_license_hint}</div>}
                {step === 6 && !signed && <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{t.contract?.step_hint || "Firma il contratto per procedere"}</div>}
                {step === 6 && signed && <div className="text-[10px] text-green-400 uppercase tracking-wider">{t.contract?.signed_short || "Contratto firmato ✓"}</div>}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

